import { useEffect, useState } from "react";
import { Link2, Copy, X, Check, ExternalLink } from "lucide-react";
import client from "../lib/api";
import { toast } from "sonner";

/*
 * UtmLinksModal — copy-paste ready UTM'd bio links for a specific video
 * (or the homepage). Fixes the #1 attribution complaint: Victoria can't
 * see where TikTok/IG traffic comes from because in-app browsers strip
 * the Referer header. UTMs survive.
 *
 * Usage: <UtmLinksModal videoId={id} onClose={fn} />
 *        <UtmLinksModal onClose={fn} />  // homepage / global bio
 */
export default function UtmLinksModal({ videoId, onClose }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    const url = videoId ? `/admin/utm-links/${videoId}` : "/admin/utm-links";
    client.get(url).then((r) => setData(r.data)).catch(() => {
      toast.error("Failed to load UTM links");
      onClose?.();
    });
  }, [videoId, onClose]);

  const copyOne = async (key, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Clipboard blocked — long-press to copy manually.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="utm-links-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-gradient-to-b from-[#151515] to-black border border-[#FFB800]/30 shadow-2xl p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 flex items-center justify-center flex-shrink-0">
              <Link2 className="w-4 h-4 text-[#FFB800]" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Attribution</p>
              <h2 className="text-white text-xl font-black leading-tight">
                {data?.video ? `UTM links · ${data.video.title}` : "UTM links · Nexus home"}
              </h2>
              <p className="text-xs text-zinc-400 mt-1 max-w-lg">
                Paste these into your TikTok / Instagram bio (or captions) so every click shows up
                as tagged traffic in Victoria&apos;s snapshot instead of &ldquo;Unknown&rdquo;.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white flex-shrink-0" data-testid="utm-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!data ? (
          <div className="py-8 text-center text-zinc-500 text-sm">Loading links…</div>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-3 text-xs">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Campaign:</span>
              <span className="font-mono text-[#FFB800] bg-black/60 px-2 py-1 rounded">{data.campaign}</span>
            </div>
            <ul className="space-y-2">
              {data.links.map((l) => (
                <li
                  key={l.key}
                  className="rounded-xl bg-zinc-900/60 border border-white/5 p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid={`utm-link-row-${l.key}`}
                >
                  <div className="flex-shrink-0 w-full sm:w-40">
                    <p className="text-xs uppercase tracking-widest font-bold text-white">{l.label}</p>
                    <p className="text-[10px] text-zinc-500">{l.utm_source} · {l.utm_medium}</p>
                  </div>
                  <input
                    readOnly
                    value={l.url}
                    onClick={(e) => e.target.select()}
                    className="flex-1 h-9 px-3 rounded-lg bg-black/60 border border-white/10 text-white text-xs font-mono truncate cursor-text focus:outline-none focus:border-[#FFB800]/60"
                    data-testid={`utm-url-${l.key}`}
                  />
                  <button
                    onClick={() => copyOne(l.key, l.url)}
                    data-testid={`utm-copy-${l.key}`}
                    className={`flex-shrink-0 h-9 px-4 rounded-lg font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2 transition-colors ${
                      copied === l.key ? "bg-emerald-500 text-black" : "bg-[#FF6B00] text-black hover:bg-[#FF6B00]/90"
                    }`}
                  >
                    {copied === l.key ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                  </button>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex-shrink-0 w-9 h-9 rounded-lg border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center"
                    title="Open"
                    data-testid={`utm-open-${l.key}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[10px] text-zinc-500 text-center">
              These links land on the same page — the <span className="font-mono text-[#FFB800]">utm_*</span> params travel through and stick to every session, giving Victoria real source data.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
